import { DomainError, invariant } from '../shared/errors';
import type { Vnd } from '../shared/money';

export const OPTION_GROUPS = ['size', 'sugar', 'ice', 'topping', 'temperature'] as const;
export type OptionGroup = (typeof OPTION_GROUPS)[number];

export const SIZES = ['M', 'L'] as const;
export type Size = (typeof SIZES)[number];

export const SUGAR_LEVELS = ['0%', '30%', '50%', '70%', '100%'] as const;
export type SugarLevel = (typeof SUGAR_LEVELS)[number];

export const ICE_LEVELS = ['Không đá', 'Ít đá', 'Bình thường'] as const;
export type IceLevel = (typeof ICE_LEVELS)[number];

export const MAX_TOPPINGS = 3;

/** Per-branch sale status, shown as "Tình trạng" in the console. */
export const STOCK_STATUSES = ['in_stock', 'low_stock', 'sold_out_today', 'discontinued'] as const;
export type StockStatus = (typeof STOCK_STATUSES)[number];

export type ProductKind = 'drink' | 'topping';

export type ProductProps = {
  id: string;
  kind: ProductKind;
  categoryId: string;
  name: string;
  description: string;
  priceM: Vnd;
  /** null = the drink is sold in one size only. */
  priceL: Vnd | null;
  /** Colours used by the cup illustration in the apps. */
  tint: string;
  background: string;
  imageUrl: string | null;
  optionGroups: OptionGroup[];
  visibleOnApp: boolean;
  bestSeller: boolean;
  stockByBranch: Record<string, StockStatus>;
  sortOrder: number;
};

export class Product {
  private constructor(private props: ProductProps) {}

  static create(props: ProductProps): Product {
    invariant(props.name.trim().length > 0, 'Tên món không được để trống');
    invariant(props.priceM > 0, 'Giá size M phải lớn hơn 0');
    invariant(props.priceL === null || props.priceL >= props.priceM, 'Giá size L phải lớn hơn hoặc bằng giá size M');
    return new Product({ ...props, name: props.name.trim() });
  }

  static restore(props: ProductProps): Product {
    return new Product(props);
  }

  get id(): string {
    return this.props.id;
  }
  get name(): string {
    return this.props.name;
  }
  get kind(): ProductKind {
    return this.props.kind;
  }

  snapshot(): ProductProps {
    return structuredClone(this.props);
  }

  priceFor(size: Size): Vnd {
    if (size === 'M') return this.props.priceM;
    if (this.props.priceL === null) throw new DomainError('VALIDATION', `${this.props.name} không có size L`);
    return this.props.priceL;
  }

  isOrderableAt(branchId: string): boolean {
    const status = this.props.stockByBranch[branchId] ?? 'in_stock';
    return this.props.visibleOnApp && (status === 'in_stock' || status === 'low_stock');
  }

  assertOrderableAt(branchId: string): void {
    if (!this.isOrderableAt(branchId)) {
      throw new DomainError('INVALID_STATE', `${this.props.name} hiện không bán tại chi nhánh này`);
    }
  }

  update(changes: Partial<Omit<ProductProps, 'id' | 'kind'>>): void {
    const next = Product.create({ ...this.props, ...changes });
    this.props = next.props;
  }

  setVisibility(visible: boolean): void {
    this.props.visibleOnApp = visible;
  }

  setStock(branchId: string, status: StockStatus): void {
    this.props.stockByBranch = { ...this.props.stockByBranch, [branchId]: status };
  }
}

export type Category = {
  id: string;
  name: string;
  sortOrder: number;
};
